import { useEffect } from 'react';
import { View, StyleSheet, AccessibilityInfo } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  cancelAnimation,
  Easing,
} from 'react-native-reanimated';
import { useTranslation } from '@dramadetector/shared';
import { GlassBackground, GlassCard } from '../glass';

interface ScreenSkeletonProps {
  variant?: 'list' | 'stats' | 'settings';
  testID?: string;
}

const BLOCK_COLOR = '#e5e7eb';

function useShimmerOpacity() {
  const opacity = useSharedValue(1);

  useEffect(() => {
    let cancelled = false;
    AccessibilityInfo.isReduceMotionEnabled().then((reduced) => {
      if (cancelled || reduced) return;
      opacity.value = withRepeat(
        withTiming(0.45, { duration: 900, easing: Easing.inOut(Easing.ease) }),
        -1,
        true,
      );
    });
    return () => {
      cancelled = true;
      cancelAnimation(opacity);
    };
  }, [opacity]);

  return useAnimatedStyle(() => ({ opacity: opacity.value }));
}

export function ScreenSkeleton({
  variant = 'list',
  testID = 'screen-skeleton',
}: ScreenSkeletonProps) {
  const { t } = useTranslation('common', { useSuspense: false });
  const shimmerStyle = useShimmerOpacity();

  const rowCount = variant === 'settings' ? 4 : variant === 'stats' ? 3 : 5;

  return (
    <GlassBackground>
      <Animated.View
        style={[styles.container, shimmerStyle]}
        testID={testID}
        accessibilityRole="none"
        accessibilityLabel={t('loading', { defaultValue: 'Loading...' })}
      >
        <View style={styles.header}>
          <View style={styles.headerPill} />
        </View>

        <View style={styles.body}>
          {Array.from({ length: rowCount }).map((_, i) => (
            <GlassCard key={i} style={styles.card}>
              <View style={styles.cardTitle} />
              <View style={styles.cardBodyShort} />
              {variant === 'list' && <View style={styles.cardBodyLong} />}
            </GlassCard>
          ))}
        </View>
      </Animated.View>
    </GlassBackground>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { alignItems: 'center', paddingTop: 16, paddingBottom: 12 },
  headerPill: {
    width: 140,
    height: 32,
    borderRadius: 999,
    backgroundColor: BLOCK_COLOR,
  },
  body: { paddingHorizontal: 16, paddingTop: 8, gap: 12 },
  card: { gap: 10 },
  cardTitle: {
    height: 16,
    width: '55%',
    borderRadius: 6,
    backgroundColor: BLOCK_COLOR,
  },
  cardBodyShort: {
    height: 12,
    width: '80%',
    borderRadius: 6,
    backgroundColor: BLOCK_COLOR,
  },
  cardBodyLong: {
    height: 12,
    width: '65%',
    borderRadius: 6,
    backgroundColor: BLOCK_COLOR,
  },
});
